<hlm-dialog [state]="state()" (closed)="onCancel()">
  <hlm-dialog-content
    *brnDialogContent="let ctx"
    class="w-[min(48rem,calc(100vw-2rem))] md:w-[min(48rem,calc(100vw-2rem))] max-w-none min-w-0 h-[700px] max-h-[90vh] flex flex-col"
  >
    <app-wizard-dialog-header
      [eyebrow]="config().eyebrow"
      [title]="config().title"
      [steps]="wizardSteps()"
      [showTimeline]="config().showTimeline !== false && totalSteps() > 1"
      (stepJump)="goToStep($event)"
    />

    <div hlmSeparator></div>
    <div class="flex-1 min-h-0 w-full">
      <div class="h-full overflow-y-auto px-1 py-5 animate-in fade-in-50 duration-300">
        <app-step-lead [text]="currentLead()" />

        <!-- Step 1: Address -->
        @if (currentStep() === 1) {
          <div class="flex flex-col gap-4">
            <div class="flex items-end gap-2.5">
              <app-field-shell class="min-w-0 flex-1" label="Adresse" required>
                <app-address-selector
                  [addresses]="addresses()"
                  [isLoading]="isAddressesLoading()"
                  [error]="addressesError()"
                  [selectedAddressId]="selectedAddress()?.addressId ?? null"
                  [totalCount]="addressTotalCount()"
                  (loadRequested)="onLoadAddressesRequested()"
                  (searchChanged)="onAddressSearch($event)"
                  (addressSelected)="onAddressSelected($event)"
                  (createNewAddress)="onCreateNewAddress()"
                  label=""
                  placeholder="Straße, PLZ oder Ort suchen"
                />
              </app-field-shell>
              <button
                type="button"
                hlmBtn
                variant="outline"
                class="h-10 shrink-0"
                (click)="onCreateNewAddress()"
              >
                <ng-icon name="lucidePlus" class="w-4 h-4 mr-1" />
                Neue Adresse
              </button>
            </div>

            <!-- Address Form Dialog -->
            @if (showAddressForm()) {
              <app-address-form
                [organizationId]="organizationId()"
                [isOpen]="showAddressForm()"
                (addressSaved)="onAddressCreated($event)"
                (close)="onAddressFormClosed()"
              />
            }

            <!-- Selected address -->
            @if (selectedAddress()) {
              <div class="rounded-xl border border-border p-4">
                <div class="flex items-start gap-3">
                  <ng-icon name="lucideMapPin" class="mt-0.5 w-4 h-4 shrink-0 text-primary" />
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-bold text-foreground">
                      {{ selectedAddress()!.street }} {{ selectedAddress()!.houseNumber }}
                    </div>
                    <div class="mt-0.5 font-mono text-xs text-muted-foreground">
                      {{ selectedAddress()!.postalCode }} {{ selectedAddress()!.city }}
                    </div>
                    <div class="mt-1 text-xs text-muted-foreground">{{ selectedAddressMeta() }}</div>
                  </div>
                  <button
                    type="button"
                    class="shrink-0 text-xs font-bold text-primary hover:text-primary/80"
                    (click)="onChangeAddress()"
                  >
                    Ändern
                  </button>
                </div>
              </div>
            }
          </div>
        }

        <!-- Step 2: Configuration -->
        @if (currentStep() === 2) {
          <form [formGroup]="analysisForm" class="grid grid-cols-2 gap-5">
            <app-suffix-input
              label="Jahresverbrauch"

              suffix="kWh/a"
              formControlName="powerConsumption"
              required
            />
            <app-suffix-input
              label="Strompreis"
              suffix="€/kWh"
              [decimals]="4"
              formControlName="electricityPrice"
              required
            />
            <div class="col-span-2">
              <app-suffix-input
                label="Quelle"
                inputType="text"
                [disabled]="true"
                [value]="creationSourceLabel()"
                helpText="Wird vom Kontext bestimmt und kann nicht geändert werden."
              />
            </div>
          </form>
        }

        <!-- Step 3: Summary -->
        @if (currentStep() === 3) {
          <div class="flex flex-col gap-6">
            <app-summary-section title="Adresse" (edit)="jumpToStep(1)">
              <app-summary-row
                label="Standort"
                [value]="selectedAddress()!.street + ' ' + selectedAddress()!.houseNumber"
              />
              <app-summary-row
                label="PLZ, Ort"
                [value]="selectedAddress()!.postalCode + ' ' + selectedAddress()!.city"
                mono
              />
              <app-summary-row label="Gebäude" [value]="selectedAddressMeta()" />
            </app-summary-section>

            <app-summary-section title="Konfiguration" (edit)="jumpToStep(2)">
              <app-summary-row label="Verbrauch" [value]="summaryConsumption()" mono />
              <app-summary-row label="Strompreis" [value]="summaryPrice()" mono />
              <app-summary-row label="Quelle" [value]="creationSourceLabel()" />
              <app-summary-row label="Status" value="Ausstehend" />
            </app-summary-section>

            <!-- Direkt ausführen -->
            <div class="rounded-xl border border-border p-4">
              <div class="flex items-start gap-3">
                <hlm-switch
                  class="mt-0.5"
                  [checked]="runImmediately()"
                  (changed)="onRunImmediatelyToggled($event)"
                />
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-bold text-foreground">Analyse direkt ausführen</div>
                  <p class="mt-0.5 text-xs leading-normal text-muted-foreground">
                    Führt die komplette Pipeline aus, von Geodaten bis Export.
                  </p>

                  @if (runImmediately()) {
                    <!-- Same pickers the campaign execution panel uses — geocut has not
                         run yet, so the type is prospective and no file can be picked. -->
                    <div class="mt-3.5 flex flex-col gap-3">
                      <div class="flex flex-col gap-1.5">
                        <label class="text-sm font-normal text-foreground">Analysetyp</label>
                        <app-analysis-type-picker
                          [selected]="runAnalysisType()"
                          (typeSelected)="runAnalysisType.set($event)"
                        />
                      </div>
                      <div class="flex flex-col gap-1.5">
                        <label class="text-sm font-normal text-foreground">Planungsvorlage</label>
                        <app-template-picker
                          [templates]="templates()"
                          [selectedTemplateId]="runTemplateId()"
                          [isLoading]="isTemplatesLoading()"
                          [error]="templatesError()"
                          [state]="runTemplateId() ? 'applied' : 'idle'"
                          (templateSelected)="runTemplateId.set($event)"
                        />
                      </div>
                      @if (!canRunImmediately()) {
                        <p class="text-xs text-muted-foreground">
                          Analysetyp und Vorlage sind für die direkte Ausführung erforderlich.
                        </p>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Error Message -->
        @if (error()) {
          <div
            class="mt-4 flex items-start gap-2.5 rounded-lg border border-destructive/20 bg-destructive/10 px-3.5 py-3"
          >
            <p class="text-xs leading-normal text-destructive">{{ error() }}</p>
          </div>
        }
      </div>
    </div>

    <div hlmSeparator></div>
    <app-wizard-dialog-footer
      [currentStep]="currentStep()"
      [totalSteps]="totalSteps()"
      [isFirstStep]="isFirstStep()"
      [isLastStep]="isLastStep()"
      [canProceed]="canProceedFromCurrentStep()"
      [isSubmitting]="isSubmitting()"
      [submitLabel]="config().submitButtonText"
      (back)="prevStep()"
      (next)="nextStep()"
      (submit)="onSubmit(ctx)"
    />
  </hlm-dialog-content>
</hlm-dialog>
